import { useEffect, useState } from "react";
import api from "@/lib/api";
import { TID } from "@/constants/testIds";
import { PriorityBadge } from "@/components/common/TrustBadge";
import { EvidenceChip } from "@/components/common/EvidenceChip";
import { DeterministicDecision, AiNarrativePlaceholder } from "@/components/common/Decision";
import { Link } from "react-router-dom";

const STATUSES = ["all", "open", "in_progress", "completed", "dismissed"];

export default function RecommendationsPage() {
  const [recs, setRecs] = useState([]);
  const [status, setStatus] = useState("all");
  const [busy, setBusy] = useState({}); // per-rec generating flag

  const load = () => api.get("/recommendations", { params: status === "all" ? {} : { status } }).then(r => setRecs(r.data));
  useEffect(() => { load(); }, [status]);  // eslint-disable-line react-hooks/exhaustive-deps

  const update = async (id, s) => {
    await api.patch(`/recommendations/${id}`, { status: s });
    load();
  };

  const generate = async (id) => {
    setBusy(b => ({ ...b, [id]: true }));
    try {
      const { data } = await api.post(`/recommendations/${id}/narrative`);
      setRecs(rs => rs.map(r => r.recommendation_id === id ? { ...r, ...data } : r));
    } catch (e) {
      console.error("narrative generation failed", e);
    } finally {
      setBusy(b => ({ ...b, [id]: false }));
    }
  };

  return (
    <div data-testid={TID.recommendationsPage} className="space-y-6 animate-fade-in-up">
      <header className="flex items-end justify-between border-b border-border pb-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-altara-teal font-semibold">Recommendations</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight text-altara-ink mt-1">Deterministic outcomes · AI narrates, rules decide</h1>
          <p className="text-sm text-muted-foreground mt-1">Every recommendation carries a rule id, version and supporting evidence.</p>
        </div>
      </header>

      <div className="flex flex-wrap gap-1.5">
        {STATUSES.map(s => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={`px-2.5 py-1 text-[10px] uppercase tracking-[0.15em] rounded-sm border ${
              status === s ? "bg-altara-deep text-white border-altara-deep" : "bg-white text-slate-600 border-slate-200 hover:border-altara-deep"
            }`}
          >
            {s.replace(/_/g, " ")}
          </button>
        ))}
      </div>

      <div data-testid={TID.recommendationsList} className="space-y-3">
        {recs.map(r => (
          <div key={r.recommendation_id} className="border border-slate-200 rounded-sm bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <PriorityBadge priority={r.priority} />
                  <span className="text-[10px] uppercase tracking-[0.15em] text-slate-500">Status</span>
                  <span className="text-[10px] uppercase tracking-[0.15em] font-semibold text-slate-800">{r.status}</span>
                  <Link to={`/consumers/${r.consumer_id}`} className="text-[10px] font-mono text-altara-teal hover:text-altara-deep">
                    · {r.consumer_id.slice(0, 12)}…
                  </Link>
                </div>
                <div className="font-display text-lg font-semibold text-altara-ink mt-1">{r.title}</div>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {["in_progress", "completed", "dismissed"].map(s => (
                  <button
                    key={s}
                    data-testid={`${TID.recUpdateStatus}-${s}`}
                    onClick={() => update(r.recommendation_id, s)}
                    className="text-[10px] uppercase tracking-[0.15em] px-2 py-1 border border-slate-200 hover:border-altara-deep hover:text-altara-deep rounded-sm text-slate-600"
                  >
                    {s.replace(/_/g, " ")}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-4 grid md:grid-cols-2 gap-3">
              <DeterministicDecision ruleId={r.rule_id} ruleVersion={r.rule_version} description={r.rule_description} action={r.action} />
              <AiNarrativePlaceholder
                isPlaceholder={r.ai_placeholder}
                narrative={r.ai_narrative}
                model={r.ai_model}
                generatedAt={r.ai_generated_at}
                onGenerate={() => generate(r.recommendation_id)}
                generating={!!busy[r.recommendation_id]}
              />
            </div>

            {r.evidence_ids?.length > 0 && (
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Supporting evidence</span>
                {r.evidence_ids.slice(0, 6).map(id => <EvidenceChip key={id} id={id} />)}
              </div>
            )}
          </div>
        ))}
        {recs.length === 0 && <div className="text-sm text-muted-foreground border border-dashed border-slate-200 rounded-sm p-8 text-center">No recommendations for this filter.</div>}
      </div>
    </div>
  );
}
